<template>
  <div class="py-5 md:px-5">
    <PageHeader class="container" title="Bookings">
      <!-- <div slot="subheader" class="mt-4 sm:mt-0">
        <NavTabs 
          :tabs="[
            { name: 'Bookings', route: { name: 'BookingList' } },
            { name: 'Orders', route: { name: 'OrderList' } },
          ]" 
        />
      </div> -->
    </PageHeader>

    <div class="container">
      <!-- Loading -->
      <Loading v-if="loading" class="p-6" />
      <!-- List -->
      <div class="card" v-else>
        <!-- Header -->
        <div class="flex flex-col justify-between p-4 border-b md:flex-row lg:p-5">
          <div class="grid grid-cols-2 gap-3 md:flex">
            <!-- Filter -->
            <FilterDropdown @apply="applyFilter" @clear="clearFilter" :loading="loading">
              <div>
                <label for="date" class="form-label">Date Range</label>
                <DatePicker  color="indigo" mode="dateTime" is-range is24hr :model-config="{ start: { timeAdjust: '00:00:00' }, end: { timeAdjust: '23:59:59' } }" v-model="filter.created_at">
                  <template v-slot="{ inputValue, inputEvents }">
                    <div class="flex justify-center">
                      <input
                        :value="inputValue.start"
                        v-on="inputEvents.start"
                        class="form-input prepend"
                      />
                      <div class="flex items-center justify-center flex-none w-10 text-gray-500 border-t border-b border-gray-300">
                        &rarr;
                      </div>
                      <input
                        :value="inputValue.end"
                        v-on="inputEvents.end"
                        class="form-input append"
                      />
                    </div>
                  </template>
                </DatePicker>
              </div>
              <div>
                <label for="customer" class="form-label">Customer</label>
                <CustomerSelect v-model="filter.customer" />
              </div>
            </FilterDropdown>
            <!-- Sort -->
            <SortDropdown :options="sortOptions" />
          </div>
        </div>

        <!-- No Results -->
        <div v-if="bookings.meta.total < 1" class="max-w-sm py-12 mx-auto text-center">
          <div class="mb-2 text-lg font-bold">
            You don't have any bookings yet.
          </div>
          <div class="text-gray-500">
            Bookings are collected via a booking form.
          </div>
        </div>

        <!-- Results -->
        <div class="divide-y" v-else>
          <!-- List -->
          <router-link v-for="booking in bookings.data" :key="booking.id" :to="{ name: 'BookingEdit', params: { id: booking.id } }" class="block text-gray-800 transition duration-150 ease-in-out hover:no-underline hover:bg-gray-50 focus:outline-none focus:bg-gray-50">
            <div class="px-4 py-4 space-y-3 md:space-y-0 md:grid md:gap-3 md:grid-cols-4 sm:px-6">
              <!-- Date & Time -->
              <div>
                <div class="font-medium" >
                  <template v-if="booking.date">
                    {{ booking.date | moment('ddd DD MMMM') }}
                  </template>
                  <template v-else>
                    No Date
                  </template>
                </div>
                <div>
                  {{ booking.time || 'No Time' }} 
                </div>
              </div>
              <!-- Customer & Status -->
              <div>
                <div class="font-medium">
                  {{ booking.customer_name }}
                </div>
                
                <div class="flex">
                  <badge :class="parseBookingStateClass(booking.status)">
                    <div slot="prefix" class="pl-2">
                      <div class="p-1 bg-current rounded-full"></div>
                    </div>
                    {{ booking.status | capitalize }}
                  </badge>
                </div>
              </div>
              <!-- Type & Addresses -->
              <div class="relative col-span-2 md:col-span-1">
                <div>
                  {{ booking.type | capitalize }}
                </div>
                <div class="flex items-center space-x-1">
                  <badge v-if="booking.origins.length" class="bg-gray-100">
                    <div class="px-2 py-1 bg-gray-200" slot="prefix">{{ booking.origins.length }}</div> 
                    {{ booking.origins.length | pluralize('Pickup') }}
                  </badge>
                  <badge v-if="booking.destinations.length" class="bg-gray-100">
                    <div class="px-2 py-1 bg-gray-200" slot="prefix">{{ booking.destinations.length }}</div>
                    {{ booking.destinations.length | pluralize(['Delivery', 'Deliveries']) }}
                  </badge>
                </div>
              </div>
              <!-- Price & Financial Status -->
              <div class="text-right">
                <div>
                  <div>{{ $currency(booking.subtotal_price / 100, account.currency) }}</div>
                </div>
              </div>
            </div>
          </router-link>
          <!-- Pagination -->
          <Pagination class="border-t rounded-b-lg" :total="bookings.meta.total" :per-page="bookings.meta.per_page" :current-page="bookings.meta.current_page" />
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { sub } from 'date-fns'
import { mapGetters } from 'vuex'
import CustomerSelect from '@/components/CustomerSelect'
import DatePicker from 'v-calendar/lib/components/date-picker.umd'
import FilterDropdown from '@/components/FilterDropdown'
import SortDropdown from '@/components/SortDropdown'

export default {
  name: 'BookingList',

  metaInfo: {
    title: 'Bookings'
  },

  components: {
    CustomerSelect,
    DatePicker,
    FilterDropdown,
    SortDropdown
  },

  created () {
    this.setFilter()
    this.getBookings()
  },

  computed: {
    ...mapGetters(['account'])
  },

  data () {
    return {
      loading: true,
      bookings: {},
      filter: {
        created_at: { start: sub(new Date(), { months: 1 }), end: new Date() }
      },
      sortOptions: [
        { label: `Date <span class="text-gray-500">(Oldest First)</span>`, prop: 'created_at', dir: 'asc' },
        { label: `Date <span class="text-gray-500">(Newest First)</span>`, prop: 'created_at', dir: 'desc' },
        { label: `Type <span class="text-gray-500">(A&rarr;Z)</span>`, prop: 'type', dir: 'desc' },
        { label: `Type <span class="text-gray-500">(Z&rarr;A)</span>`, prop: 'type', dir: 'asc' },
        { label: `Address <span class="text-gray-500">(A&rarr;Z)</span>`, prop: 'formatted_address', dir: 'asc' },
        { label: `Address <span class="text-gray-500">(Z&rarr;A)</span>`, prop: 'formatted_address', dir: 'desc' },
        { label: `ID <span class="text-gray-500">(A&rarr;Z)</span>`, prop: 'orderid', dir: 'asc' },
        { label: `ID <span class="text-gray-500">(Z&rarr;A)</span>`, prop: 'orderid', dir: 'desc' },
      ]
    }
  },

  methods: {
    async getBookings () {
      try {
        const { data } = await this.$api.get(`account/${this.$route.params.account}/bookings`, {
          params: {
            page: this.$route.query.page || 1,
            sort: this.$route.query.sort || 'created_at',
            dir: this.$route.query.dir || 'desc',
            ...this.filter && { filter: this.filter }
          }
        })
        this.bookings = data.bookings
        this.loading = false
      } catch (error) {
        console.error(error)
      }
    },

    setFilter () {
      if (this.$route.query.filter) {
        this.filter = JSON.parse(this.$route.query.filter)
      }
    },

    applyFilter () {
      this.$router.push({ 
        query: { 
          ...this.$route.query, 
          filter: JSON.stringify(this.filter)
        } 
      })
    },

    clearFilter () {
      this.filter = {
        created_at: { start: sub(new Date(), { years: 1 }), end: new Date() }
      }
      this.$router.push({ 
        query: { 
          ...this.$route.query, 
          filter: undefined
        } 
      })
    },

    parseBookingStateClass (status) {
      const classes = {
        pending: 'text-gray-500 bg-gray-100',
        draft: 'text-yellow-500 bg-yellow-100',
        approved: 'text-blue-500 bg-blue-100',
        cancelled: 'text-red-500 bg-red-100',
        complete: 'text-emerald-500 bg-emerald-100',
      }
      return classes[status]
    }
  },

  watch: {
    '$route.query': {
      handler (newVal, oldVal) {
        this.getBookings()
      },
      deep: true
    }
  }
}
</script>